import { EqlFieldValuesAutoComplete } from '@modules/common/components/EqlFieldValuesAutoComplete';
import {
  getFallbackDisplayNameForEqlField,
  getFilterValueFromType,
  upsertQueryFilterNode,
} from '@modules/queryBuilder/helpers';
import { QueryBuilderActionType } from '@modules/queryBuilder/store/queryBuilderActionType';
import { useQueryBuilderStore } from '@modules/queryBuilder/store/useQueryBuilderStore';
import { EqlFieldValue } from 'generated/graphql';
import { NormalisedFilter } from 'v2/querybuilder/types/querystore';
import { QueryFilterNLSAutoComplete } from '../QueryFilterNLSAutoComplete';
import { QueryFilterSingleSelect } from '../QueryFilterSingleSelect';

interface QueryFilterValueDropdownProps {
  id?: string;
  open: boolean;
  onClose: () => void;
  filter: NormalisedFilter;
  isNLS: boolean;
  shouldUseInputValues: boolean;
  selectedValues: EqlFieldValue[];
  shouldAllowMultipleValues: boolean;
  value: EqlFieldValue | EqlFieldValue[] | undefined;
}

export const QueryFilterValueDropdown = ({
  id,
  open,
  onClose,
  filter,
  isNLS,
  shouldUseInputValues,
  selectedValues,
  shouldAllowMultipleValues,
  value,
}: QueryFilterValueDropdownProps) => {
  const nodes = useQueryBuilderStore(({ query }) => query.nodes);
  const dispatch = useQueryBuilderStore(({ dispatch }) => dispatch);
  const eqlFieldByFieldID = useQueryBuilderStore(
    ({ metadata }) => metadata.eqlFieldByFieldID,
  );

  const fieldDisplayName =
    eqlFieldByFieldID[filter.field]?.field?.displayName ??
    getFallbackDisplayNameForEqlField({ field: filter.field });

  const onValueChange = (value: EqlFieldValue | EqlFieldValue[] | null) => {
    const updatedNodes = upsertQueryFilterNode({
      node: {
        ...filter,
        value: getFilterValueFromType({
          value: Array.isArray(value)
            ? value.map(({ value }) => value)
            : value?.value ?? '',
          type: filter.value.type,
        }),
      },
      nodesByID: nodes,
      parentID: filter.parent,
    });

    dispatch({
      type: QueryBuilderActionType.SET_QUERY,
      payload: {
        nodes: updatedNodes,
      },
    });
    const eqlFieldValueById: Record<string, EqlFieldValue> = {};

    if (Array.isArray(value)) {
      value.forEach((item) => {
        eqlFieldValueById[item.value] = item;
      });
    } else if (value) {
      eqlFieldValueById[value.value] = value;
    }

    dispatch({
      type: QueryBuilderActionType.SET_METADATA,
      payload: {
        eqlFieldByFieldID: {
          ...eqlFieldByFieldID,
          [filter.field]: {
            ...eqlFieldByFieldID[filter.field],
            eqlFieldValueByID: {
              ...eqlFieldByFieldID[filter.field]?.eqlFieldValueByID,
              ...eqlFieldValueById,
            },
          },
        },
      },
    });
  };

  const clearFilterIDUnderInspect = () => {
    dispatch({
      type: QueryBuilderActionType.SET_METADATA,
      payload: {
        filterIDUnderInspect: null,
      },
    });
  };

  if (isNLS) {
    return <QueryFilterNLSAutoComplete filter={filter} isMenuOpen={open} />;
  }

  if (shouldUseInputValues) {
    // it is going to be single select. so we can just use the first value
    const firstValue = selectedValues[0];
    const singleValue = firstValue
      ? {
          value: firstValue.value,
          id: firstValue.value,
          displayName: firstValue.displayName,
        }
      : null;

    return (
      <QueryFilterSingleSelect
        id={id}
        open={open}
        operator={filter.operator}
        filterField={filter}
        value={singleValue}
        onClose={() => {
          onClose();
          clearFilterIDUnderInspect();
        }}
      />
    );
  }

  return (
    <EqlFieldValuesAutoComplete
      id={id}
      multiple={shouldAllowMultipleValues}
      open={open}
      disablePortal={true}
      onClose={() => {
        onClose();
        clearFilterIDUnderInspect();
      }}
      onChange={onValueChange}
      value={value}
      filterField={filter.field}
      fieldDisplayName={fieldDisplayName}
    />
  );
};
